import React, { useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  ChevronDown,
  Dropdown,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
  Icon,
} from '@mesh.ai/rabbit';
import { useReviewCreationData } from 'reducers/reviewCreationSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useTemplateMetadata } from '../../../Hooks/dataHooks/shareResultsDataHooks';

export const ResponseTableDropdown = ({ options = [], defaultValue = { label: '', value: '' }, disabled = false }) => {
  const [selectedResponseType, setSelectedResponseType] = useState(defaultValue.value ? defaultValue : options?.[0]);

  return (
    <Dropdown modal={false}>
      {/* // To do : extract this style to a separate component and use it in other places also (like intent dropdown) */}
      <Box
        sx={{
          'button[data-state="open"]': {
            borderBottom: '1px solid #0D819F',
          },
          'button[data-state="closed"]': {
            borderBottom: '1px dashed #999999',
          },
          'button[data-state="open"] .icon': {
            color: '#0D819F',
            transform: 'rotate(180deg)',
            transition: 'transform 0.2s',
          },
          "button[data-state='closed'] .icon": {
            color: '#333333',
          },
        }}
      >
        <DropdownTrigger disabled={disabled} style={{ opacity: disabled ? '0.4' : '1' }}>
          <Flex alignItems="center">
            <TextBodyMedium>{selectedResponseType.label}</TextBodyMedium>
            <Box ml="auto">
              <Box>
                <Icon className="icon" IconSVG={ChevronDown} />
              </Box>
            </Box>
          </Flex>
        </DropdownTrigger>
      </Box>

      <DropdownContent align="start" style={{ minWidth: '200px' }}>
        <DropdownRadioGroup
          value={selectedResponseType.value}
          onValueChange={(value) => {
            setSelectedResponseType(options.find((opt) => opt.value === value));
          }}
        >
          {options.map((response) => (
            <DropdownRadioItem key={response.value} value={response.value}>
              {response.label}
            </DropdownRadioItem>
          ))}
        </DropdownRadioGroup>
      </DropdownContent>
    </Dropdown>
  );
};

const AnonymityDropdown = ({
  data,
}: {
  data: { anonymity: { checked: boolean; disabled: boolean }; reviewer: string };
}) => {
  const { anonymity, reviewer } = data;
  const anonymityOptions = [
    { value: 'Revealed', label: 'Revealed' },
    { value: 'Anonymous', label: 'Anonymous' },
  ];

  const isAnonymityNotApplicable = reviewer === 'Dotted line manager' || reviewer === 'Manager';
  if (isAnonymityNotApplicable) {
    return null;
  }
  return (
    <ResponseTableDropdown
      options={anonymityOptions}
      defaultValue={anonymity.checked ? anonymityOptions[1] : anonymityOptions[0]}
      disabled={anonymity.disabled}
    />
  );
};

const ReviewerResponseTypeDropdown = () => {
  const reviewerResponseOptions = [
    { value: 'Comments & ratings', label: 'Comments & ratings' },
    { value: 'Comments only', label: 'Comments only' },
    { value: 'Rating only', label: 'Rating only' },
    { value: 'Not visible', label: 'Not visible' },
  ];
  return <ResponseTableDropdown options={reviewerResponseOptions} />;
};

const ReviewerCellRenderer = ({ value }) => <TextBodyMedium>{value}</TextBodyMedium>;

export const ReviewerTypeVisibilitySettingsSelfDemo = ({ feedbackFormId }) => {
  const { reviewCycleId } = useReviewCreationData();

  const [rowsData, setRowsData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);

  const selfTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.SELF,
  });

  const hasGoalQuestions = selfTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const hasCompetencyQuestions =
    selfTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;
  const hasQNAQuestions = selfTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.QNA > 0;

  const hasManager = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.MANAGER,
  });
  const hasPeer = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.PEER,
  });
  const hasUpward = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.UPWARD,
  });
  const hasDLM = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.DLM,
  });

  const commonColumnsDef = useMemo(
    () => ({
      headerComponent: CustomHeaderCell,
      headerComponentParams: {
        enableMenu: false,
        enableSorting: false,
      },
      cellStyle: { display: 'flex', alignItems: 'center', height: '100%' },
    }),
    []
  );

  interface Anonymity {
    checked: boolean;
    disabled: boolean;
  }

  interface RowData {
    reviewer: string;
    goal: string;
    competency: string;
    qna: string;
    anonymity: Anonymity;
    isVisible: boolean;
  }

  interface ColumnDef {
    colId: string;
    field: string;
    displayName: string;
    minWidth: number;
    flex?: number;
    hide?: boolean;
    cellRenderer: (params: unknown) => JSX.Element;
  }

  const defaultColumnsDef: ColumnDef[] = useMemo(
    () => [
      {
        colId: 'Reviewer',
        field: 'reviewer',
        displayName: 'Reviewer',
        minWidth: 150,
        flex: 1,
        cellRenderer: ReviewerCellRenderer,
      },
      {
        colId: 'Goal',
        field: 'goal',
        displayName: 'Goal section',
        width: 180,
        minWidth: 180,
        hide: !hasGoalQuestions,
        cellRenderer: ReviewerResponseTypeDropdown,
      },
      {
        colId: 'Competency',
        field: 'competency',
        displayName: 'Competency section',
        width: 180,
        minWidth: 180,
        hide: !hasCompetencyQuestions,
        cellRenderer: ReviewerResponseTypeDropdown,
      },
      {
        colId: 'Q&A',
        field: 'qna',
        displayName: 'Q&A section',
        width: 180,
        minWidth: 180,
        hide: !hasQNAQuestions,
        cellRenderer: ReviewerResponseTypeDropdown,
      },
      {
        colId: 'Anonymity',
        field: 'anonymity',
        displayName: 'Reviewer identity',
        width: 150,
        minWidth: 150,
        cellRenderer: AnonymityDropdown,
      },
    ],
    [hasGoalQuestions, hasCompetencyQuestions, hasQNAQuestions]
  );

  const defaultRowsData: RowData[] = useMemo(
    () => [
      {
        reviewer: 'Manager',
        goal: 'Comments & ratings',
        competency: 'Comments & ratings',
        qna: 'Comments & ratings',
        anonymity: { checked: true, disabled: false },
        isVisible: Boolean(hasManager),
      },
      {
        reviewer: 'Dotted line manager',
        goal: 'Comments & ratings',
        competency: 'Comments & ratings',
        qna: 'Comments & ratings',
        anonymity: { checked: true, disabled: false },
        isVisible: Boolean(hasDLM),
      },
      {
        reviewer: 'Upward',
        goal: 'Comments & ratings',
        competency: 'Comments & ratings',
        qna: 'Comments & ratings',
        anonymity: { checked: true, disabled: true },
        isVisible: Boolean(hasUpward),
      },
      {
        reviewer: 'Peer',
        goal: 'Comments & ratings',
        competency: 'Comments & ratings',
        qna: 'Comments & ratings',
        anonymity: { checked: true, disabled: false },
        isVisible: Boolean(hasPeer),
      },
    ],
    [hasDLM, hasManager, hasPeer, hasUpward]
  );

  useEffect(() => {
    const updatedColumns = defaultColumnsDef
      .filter((column) => !column.hide)
      .map((column) => ({ ...column, ...commonColumnsDef }));

    setColumnDefs(updatedColumns);
  }, [commonColumnsDef, defaultColumnsDef, hasCompetencyQuestions, hasGoalQuestions, hasQNAQuestions]);

  useEffect(() => {
    const updatedRows = defaultRowsData.filter((row) => row.isVisible);

    setRowsData(updatedRows);
  }, [commonColumnsDef, defaultColumnsDef, defaultRowsData, hasCompetencyQuestions, hasGoalQuestions, hasQNAQuestions]);

  return (
    <AccordionPanel2 pl="32px !important" mb="10px">
      <Flex flexDir="column" gap={3}>
        <Flex alignItems="center" gap={2}>
          <TextBodyMedium>Set the visibility of other reviewer&apos;s responses for self</TextBodyMedium>
          <TooltipInfoV1 title="Post the review publish, visibility of manager answers to the employees (answer means the comment and rating against a question)" />
        </Flex>
        <Box className="auto-min-height-aggrid">
          <AgGridTable
            rowData={rowsData}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            minHeight="auto"
            containerAutoHeight
            suppressScrollOnNewData
            kebabMenuIsRowSpecific={false}
            gridOptions={{
              rowHeight: 60,
              headerHeight: 60,
            }}
          />
        </Box>
      </Flex>
    </AccordionPanel2>
  );
};
